import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Modal, Button, Spin, Typography, Tag, Tooltip } from "antd";
import {
  DollarOutlined,
  UserOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  ExportOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { INVOICE_PREVIEW_QUERY } from "./InvoicePreview.gql";
import {
  InvoicePreviewProps,
  InvoiceDetails,
  InvoiceDetailsResponse,
} from "./InvoicePreview.types";
import {
  PreviewContainer,
  AnalyticsSection,
  AnalyticsHeader,
  AnalyticsGrid,
  AnalyticsCard,
  AnalyticsLabel,
  AnalyticsValue,
  RatioBar,
  RatioSegment,
  RatioLegend,
  LegendItem,
  LegendDot,
  DetailsSection,
  DetailsSectionTitle,
  DetailsGrid,
  DetailRow,
  DetailLabel,
  DetailValue,
  PatientSection,
  PatientHeader,
  PatientAvatar,
  PatientInfo,
  OperationsSection,
  OperationsList,
  OperationItem,
  OperationName,
  OperationCode,
  OperationPrice,
  StatusBadge,
  PaymentsSection,
  PaymentsSummary,
  PaymentTotal,
  PaymentRemaining,
  PaymentProgressBar,
  PaymentProgress,
  FooterActions,
  LoadingContainer,
  ErrorContainer,
  EInvoiceSection,
  EInvoiceCard,
} from "./InvoicePreview.styled";
import { addTranslationResources } from "./InvoicePreview.translations";

const { Text } = Typography;

const formatCurrency = (amount: number, currency?: string): string => {
  const formattedAmount = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currency || "SAR"} ${formattedAmount}`;
};

const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return (first + last).toUpperCase() || "?";
};

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoiceId,
  visible,
  onClose,
  i18next,
  t,
  navigate,
  currency = "SAR",
}) => {
  // Add the popup's own translations before the first render: they do not depend on the
  // namespace of the feature that opens it
  useMemo(() => {
    if (i18next) addTranslationResources(i18next);
  }, [i18next]);

  const { data, loading, error } = useQuery<InvoiceDetailsResponse>(
    INVOICE_PREVIEW_QUERY,
    {
      variables: { invoice: invoiceId },
      skip: !visible || !invoiceId,
      fetchPolicy: "cache-and-network",
    }
  );

  const invoice: InvoiceDetails | null = data?.invoiceDetails || null;

  // Calculate analytics
  const analytics = useMemo(() => {
    if (!invoice) return null;

    const total = invoice.total || 0;
    const insuranceAmount = invoice.insurance || 0;
    // Calculate patient amount: use patientPart if it's a positive number, otherwise calculate from total - insurance
    const patientAmount = (invoice.patientPart && invoice.patientPart > 0) 
      ? invoice.patientPart 
      : (total - insuranceAmount);

    // Calculate the patient/insurance split based on what each should pay
    const totalResponsibility = patientAmount + insuranceAmount;
    let patientRatio = 0;
    let insuranceRatio = 0;
    
    if (totalResponsibility > 0) {
      patientRatio = Math.round((patientAmount / totalResponsibility) * 100);
      insuranceRatio = 100 - patientRatio; // Ensure they add up to 100%
    }

    const paidAmount = invoice.paid || 0;
    const paymentRatio = total > 0 ? Math.round((paidAmount / total) * 100) : 0;
    const remaining = total - paidAmount;

    return {
      patientAmount,
      insuranceAmount,
      patientRatio,
      insuranceRatio,
      paidAmount,
      paymentRatio,
      remaining,
      total,
    };
  }, [invoice]);

  const handleViewDetails = () => {
    navigate(`/finances/invoice/${invoiceId}`);
    onClose();
  };

  const renderEInvoiceStatus = (status: string | null | undefined): {
    label: string;
    submitted: boolean;
  } => {
    switch (status) {
      case "SUBMITTED":
        return { label: t("invoicePreview:einvoice_submitted"), submitted: true };
      case "PENDING":
        return { label: t("invoicePreview:einvoice_pending"), submitted: false };
      case "IN_PROGRESS":
        return { label: t("invoicePreview:einvoice_in_progress"), submitted: false };
      case "FAILED":
        return { label: t("invoicePreview:einvoice_failed"), submitted: false };
      case "CANCELLED":
        return { label: t("invoicePreview:einvoice_cancelled"), submitted: false };
      default:
        return { label: t("invoicePreview:einvoice_not_submitted"), submitted: false };
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <WarningOutlined style={{ fontSize: 32 }} />
          <Text type="danger">{t("invoicePreview:error_loading_invoice")}</Text>
        </ErrorContainer>
      );
    }

    if (!invoice) {
      return (
        <ErrorContainer>
          <InfoCircleOutlined style={{ fontSize: 32, color: "#6b7280" }} />
          <Text type="secondary">{t("invoicePreview:invoice_not_found")}</Text>
        </ErrorContainer>
      );
    }

    const patientStatus = renderEInvoiceStatus(invoice.eInvoiceStatus);
    const claimInvoice = invoice.claim?.activeClaimInvoice;
    const claimStatus = renderEInvoiceStatus(claimInvoice?.status);
    const hasInsurance = (invoice.insurance || 0) > 0;

    return (
      <PreviewContainer>
        {/* Analytics Section */}
        <AnalyticsSection>
          <AnalyticsHeader>
            <h4>{t("invoicePreview:invoice_analytics")}</h4>
            <Tag color="white" style={{ color: "#667eea" }}>
              #{invoice.referenceId}
            </Tag>
          </AnalyticsHeader>

          <AnalyticsGrid>
            <AnalyticsCard>
              <AnalyticsLabel>{t("invoicePreview:patient_to_insurance")}</AnalyticsLabel>
              <AnalyticsValue>
                {analytics?.patientRatio}% / {analytics?.insuranceRatio}%
              </AnalyticsValue>
              <RatioBar>
                <RatioSegment
                  $width={analytics?.patientRatio || 0}
                  $color="#60a5fa"
                />
                <RatioSegment
                  $width={analytics?.insuranceRatio || 0}
                  $color="#f472b6"
                />
              </RatioBar>
              <RatioLegend>
                <LegendItem>
                  <LegendDot $color="#60a5fa" />
                  <span>{t("invoicePreview:patient")}</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#f472b6" />
                  <span>{t("invoicePreview:insurance")}</span>
                </LegendItem>
              </RatioLegend>
            </AnalyticsCard>

            <AnalyticsCard $variant="secondary">
              <AnalyticsLabel>{t("invoicePreview:payment_progress")}</AnalyticsLabel>
              <AnalyticsValue>
                {analytics?.paymentRatio}% {t("invoicePreview:paid")}
              </AnalyticsValue>
              <RatioBar>
                <RatioSegment
                  $width={analytics?.paymentRatio || 0}
                  $color="#34d399"
                />
              </RatioBar>
              <RatioLegend>
                <LegendItem>
                  <span>
                    {formatCurrency(analytics?.paidAmount || 0, currency)} /{" "}
                    {formatCurrency(analytics?.total || 0, currency)}
                  </span>
                </LegendItem>
              </RatioLegend>
            </AnalyticsCard>
          </AnalyticsGrid>
        </AnalyticsSection>

        {/* Patient Section */}
        {invoice.patient && (
          <PatientSection>
            <PatientHeader>
              <PatientAvatar>
                {getInitials(invoice.patient.firstName, invoice.patient.lastName)}
              </PatientAvatar>
              <PatientInfo>
                <div className="patient-name">
                  {invoice.patient.firstName} {invoice.patient.lastName}
                </div>
                <div className="patient-id">
                  #{invoice.patient.referenceId}
                  {invoice.patient.phoneNumber && ` • ${invoice.patient.phoneNumber}`}
                </div>
              </PatientInfo>
            </PatientHeader>
            <DetailsGrid>
              {invoice.patient.nationalId && (
                <DetailRow>
                  <DetailLabel>{t("invoicePreview:national_id")}</DetailLabel>
                  <DetailValue>{invoice.patient.nationalId}</DetailValue>
                </DetailRow>
              )}
              {invoice.patient.insuranceNumber && (
                <DetailRow>
                  <DetailLabel>{t("invoicePreview:insurance_number")}</DetailLabel>
                  <DetailValue>{invoice.patient.insuranceNumber}</DetailValue>
                </DetailRow>
              )}
            </DetailsGrid>
          </PatientSection>
        )}

        {/* Invoice Details Section */}
        <DetailsSection>
          <DetailsSectionTitle>
            <FileTextOutlined />
            {t("invoicePreview:invoice_details")}
          </DetailsSectionTitle>
          <DetailsGrid>
            <DetailRow>
              <DetailLabel>{t("invoicePreview:subtotal")}</DetailLabel>
              <DetailValue>{formatCurrency(invoice.subtotal, currency)}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{t("invoicePreview:discount")}</DetailLabel>
              <DetailValue $type="success">
                -{formatCurrency(invoice.discount, currency)} ({invoice.discountPercent}%)
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{t("invoicePreview:tax")}</DetailLabel>
              <DetailValue>
                {formatCurrency(invoice.tax, currency)} ({invoice.taxPercent}%)
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>{t("invoicePreview:total")}</DetailLabel>
              <DetailValue $type="primary">
                {formatCurrency(invoice.total, currency)}
              </DetailValue>
            </DetailRow>
            {invoice.insuranceCompany && (
              <DetailRow>
                <DetailLabel>{t("invoicePreview:insurance_company")}</DetailLabel>
                <DetailValue>{invoice.insuranceCompany.name}</DetailValue>
              </DetailRow>
            )}
            {invoice.doctor && (
              <DetailRow>
                <DetailLabel>{t("invoicePreview:doctor")}</DetailLabel>
                <DetailValue>{invoice.doctor.name}</DetailValue>
              </DetailRow>
            )}
            {invoice.branch && (
              <DetailRow>
                <DetailLabel>{t("invoicePreview:branch")}</DetailLabel>
                <DetailValue>{invoice.branch.name}</DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>{t("invoicePreview:created_at")}</DetailLabel>
              <DetailValue>
                {dayjs(invoice.createdAt).format("YYYY-MM-DD HH:mm")}
              </DetailValue>
            </DetailRow>
          </DetailsGrid>
        </DetailsSection>

        {/* Payment Section */}
        <PaymentsSection>
          <PaymentsSummary>
            <PaymentTotal>
              <div className="label">{t("invoicePreview:total_paid")}</div>
              <div className="amount">{formatCurrency(invoice.paid, currency)}</div>
            </PaymentTotal>
            <PaymentRemaining>
              <div className="label">{t("invoicePreview:remaining")}</div>
              <div className="amount">
                {formatCurrency(analytics?.remaining || 0, currency)}
              </div>
            </PaymentRemaining>
          </PaymentsSummary>
          <PaymentProgressBar>
            <PaymentProgress $percent={analytics?.paymentRatio || 0} />
          </PaymentProgressBar>
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
            }}
          >
            <Text type="secondary">
              {invoice.payments?.length || 0} {t("invoicePreview:payments")}
            </Text>
            {invoice.paidInFull ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>
                {t("invoicePreview:paid_in_full")}
              </Tag>
            ) : invoice.pendingPayment > 0 ? (
              <Tag color="warning" icon={<WarningOutlined />}>
                {t("invoicePreview:pending_payment")}
              </Tag>
            ) : null}
          </div>
        </PaymentsSection>

        {/* Operations Section */}
        {invoice.operations && invoice.operations.length > 0 && (
          <OperationsSection>
            <DetailsSectionTitle>
              <MedicineBoxOutlined />
              {t("invoicePreview:operations")} ({invoice.operations.length})
            </DetailsSectionTitle>
            <OperationsList>
              {invoice.operations.slice(0, 5).map((op) => (
                <OperationItem key={op.id}>
                  <div>
                    <StatusBadge $status={op.status}>
                      {op.status?.slice(0, 3)}
                    </StatusBadge>
                    <OperationName>{op.name}</OperationName>
                    <OperationCode>({op.code})</OperationCode>
                  </div>
                  <OperationPrice>
                    {formatCurrency(op.price * op.amount - op.discount, currency)}
                  </OperationPrice>
                </OperationItem>
              ))}
              {invoice.operations.length > 5 && (
                <Text type="secondary" style={{ textAlign: "center", padding: 8 }}>
                  +{invoice.operations.length - 5} {t("invoicePreview:more_operations")}
                </Text>
              )}
            </OperationsList>
          </OperationsSection>
        )}

        {/* E-Invoice Status Section */}
        <EInvoiceSection>
          <EInvoiceCard $submitted={patientStatus.submitted} $fullWidth={!hasInsurance}>
            <div className="label">{t("invoicePreview:patient_einvoice")}</div>
            <div className="status">
              {patientStatus.submitted ? (
                <CheckCircleOutlined style={{ marginRight: 4 }} />
              ) : (
                <CloseCircleOutlined style={{ marginRight: 4 }} />
              )}
              {patientStatus.label}
            </div>
          </EInvoiceCard>
          {/* Insurance is reported on the claim's ZATCA invoice, not per invoice */}
          {hasInsurance && (
            <EInvoiceCard $submitted={claimStatus.submitted}>
              <div className="label">
                {invoice.claim
                  ? `${t("invoicePreview:claim", "Claim")}: ${invoice.claim.name || `#${invoice.claim.referenceId || invoice.claim.id.substring(0, 8)}`}`
                  : t("invoicePreview:claim", "Claim")}
              </div>
              <div className="status">
                {claimStatus.submitted ? (
                  <CheckCircleOutlined style={{ marginRight: 4 }} />
                ) : (
                  <CloseCircleOutlined style={{ marginRight: 4 }} />
                )}
                {!invoice.claim
                  ? t("invoicePreview:not_in_claim", "Not in a claim")
                  : claimInvoice
                    ? `${claimInvoice.number} · ${claimStatus.label}`
                    : t("invoicePreview:claim_invoice_not_issued", "Claim invoice not issued")}
              </div>
            </EInvoiceCard>
          )}
        </EInvoiceSection>

        {/* Footer Actions */}
        <FooterActions>
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={handleViewDetails}
            size="large"
          >
            {t("invoicePreview:view_full_details")}
          </Button>
        </FooterActions>
      </PreviewContainer>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DollarOutlined />
          <span>{t("invoicePreview:invoice_preview")}</span>
        </div>
      }
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default InvoicePreview;

